import { capitalize } from "lodash";
import React, { useMemo } from "react";
import { Helmet } from "react-helmet";
import { IntercomProvider } from "react-intercom-hook";
import { useLocation } from "react-router-dom";
import { AppearanceProvider } from "./appearance";
import { AuthenticationProvider } from "./authentication";
import { DiscordChannelsProvider } from "./discordChannels";
import { DropdownMenuProvider } from "./dropdownMenu";
import { GeolocationProvider } from "./geolocation";
import { GoogleAnalyticsProvider } from "./googleAnalytics";
import { GoogleMapsWrapperProvider } from "./googleMaps";
import { HeliumDataProvider } from "./heliumData";
import { LoadingProvider } from "./loading";
import { StatsProvider } from "./stats";
import { ToastProvider } from "./toast";
import { ViewportProvider } from "./viewport";
import { WorkspacesProvider } from "./workspaces";

interface Props {}

const AppContext: React.FC<Props> = ({ children }) => {
  const location = useLocation();

  const title = useMemo(() => {
    const titleSections = location.pathname
      .split("/")
      .filter((item) => item.length && item.length < 14)
      .map(capitalize);

    return ["Hotspotty"].concat(titleSections).join(" - ");
  }, [location]);

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <GoogleAnalyticsProvider>
        <IntercomProvider
          autoBoot
          appId={process.env.REACT_APP_INTERCOM_KEY as string}
        >
          <LoadingProvider>
            <HeliumDataProvider>
              <StatsProvider>
                <AppearanceProvider>
                  <ToastProvider delay={3000}>
                    <AuthenticationProvider>
                      <WorkspacesProvider>
                        <DiscordChannelsProvider>
                          <ViewportProvider>
                            <GeolocationProvider>
                              <DropdownMenuProvider>
                                <GoogleMapsWrapperProvider
                                  apiKey={process.env.REACT_APP_GMAPS_KEY ?? ""}
                                >
                                  {children}
                                </GoogleMapsWrapperProvider>
                              </DropdownMenuProvider>
                            </GeolocationProvider>
                          </ViewportProvider>
                        </DiscordChannelsProvider>
                      </WorkspacesProvider>
                    </AuthenticationProvider>
                  </ToastProvider>
                </AppearanceProvider>
              </StatsProvider>
            </HeliumDataProvider>
          </LoadingProvider>
        </IntercomProvider>
      </GoogleAnalyticsProvider>
    </>
  );
};

export default AppContext;
